import React, { memo, useCallback, useMemo, useState } from 'react'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import Divider from '@material-ui/core/Divider'
import Grid from '@material-ui/core/Grid'
import LockOutlinedIcon from '@material-ui/icons/LockOutlined'
import Typography from '@material-ui/core/Typography'
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement
} from '@stripe/react-stripe-js'
import Popup from '../Popup'
import LOG from '../../logging/Logger'

const useOptions = () => {
  const theme = useTheme()
  const options = useMemo(
    () => ({
      style: {
        base: {
          fontSize: theme.typography.fontSize,
          color: theme.palette.text.primary,
          letterSpacing: 'inherit',
          fontFamily: theme.typography.fontFamily,
          '::placeholder': {
            color: theme.palette.text.hint
          }
        },
        invalid: {
          color: theme.palette.error.main,
          iconColor: '#fa755a'
        }
      }
    }),
    [theme]
  )

  return options
}

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    margin: 'auto'
  },
  formControl: {
    marginTop: theme.spacing(2),
    minWidth: 120
  },
  formControlLabel: {
    marginTop: theme.spacing(1)
  }
}))

const SplitForm = memo(({ handler, setOpen, open, period }) => {
  LOG.debug('Render: CheckoutForm: SplitForm')

  const classes = useStyles()

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const stripe = useStripe()
  const elements = useElements()
  const options = useOptions()

  const [showErrorMsg, setShowErrorMsg] = useState(false)
  const [errorMsg, setErrorMsg] = useState(
    'There was an error with your payment details'
  )

  const handleErrorClose = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return
    }

    setShowErrorMsg(false)
  }, [])

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault()

      if (!stripe || !elements) {
        // Stripe.js has not loaded yet. Make sure to disable
        // form submission until Stripe.js has loaded.
        return
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardNumberElement)
      })

      LOG.debug('[PaymentMethod]', paymentMethod)
      LOG.debug('[error]', error)

      if (error) {
        LOG.debug('[error]', error)
        setErrorMsg(error.message)
        setShowErrorMsg(true)
      } else {
        LOG.debug('[PaymentMethod]', paymentMethod)
        handler(paymentMethod)
      }
    },
    [elements, handler, stripe]
  )

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='form-dialog-title'
    >
      <DialogTitle id='form-dialog-title'>
        <LockOutlinedIcon /> Subscribe
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Please enter your card details to start your {period} subscription
        </DialogContentText>
        <form onSubmit={handleSubmit} className={classes.form}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <label className={classes.formControl}>
                <Typography gutterBottom>Card number</Typography>
                <CardNumberElement
                  options={options}
                  onReady={() => {
                    LOG.debug('CardNumberElement [ready]')
                  }}
                  onChange={event => {
                    LOG.debug('CardNumberElement [change]', event)
                  }}
                  onBlur={() => {
                    LOG.debug('CardNumberElement [blur]')
                  }}
                  onFocus={() => {
                    LOG.debug('CardNumberElement [focus]')
                  }}
                />
              </label>
            </Grid>
            <Grid item xs={12}>
              <label>
                <Typography gutterBottom>Expiration date</Typography>
                <CardExpiryElement
                  options={options}
                  onReady={() => {
                    LOG.debug('CardNumberElement [ready]')
                  }}
                  onChange={event => {
                    LOG.debug('CardNumberElement [change]', event)
                  }}
                  onBlur={() => {
                    LOG.debug('CardNumberElement [blur]')
                  }}
                  onFocus={() => {
                    LOG.debug('CardNumberElement [focus]')
                  }}
                />
              </label>
            </Grid>
            <Grid item xs={12}>
              <label>
                <Typography gutterBottom>CVC</Typography>
                <CardCvcElement
                  options={options}
                  onReady={() => {
                    LOG.debug('CardNumberElement [ready]')
                  }}
                  onChange={event => {
                    LOG.debug('CardNumberElement [change]', event)
                  }}
                  onBlur={() => {
                    LOG.debug('CardNumberElement [blur]')
                  }}
                  onFocus={() => {
                    LOG.debug('CardNumberElement [focus]')
                  }}
                />
              </label>
            </Grid>
            <Grid item xs={12}>
              <br />
              <Divider />
            </Grid>
          </Grid>
        </form>
        <Popup
          open={showErrorMsg}
          message={errorMsg}
          onClose={handleErrorClose}
          variant='error'
          autoHideDurationOverride={6000}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color='primary'>
          Cancel
        </Button>
        <Button onClick={handleSubmit} color='secondary' disabled={!stripe}>
          Subscribe
        </Button>
      </DialogActions>
    </Dialog>
  )
})

export default SplitForm
